import cloneDeep from 'lodash/cloneDeep';
import { SearchDateOption, SortOptions, SortOrder, StaticFilterKeywords, type SearchCategoryCount, type SortParameters } from '@mimir/common/dto/searchModels';
import type { SearchParams, SearchState as ItemSearchState } from '@mimir/web/store/interfaces';
import { ViewType } from '@mimir/web/pages/search/constants';
import type { ClientItem, SearchResultHit } from '@mimir/common/dto/searchResultHit';
import Vue, { reactive } from 'vue';
import { ArchiveRestoreStatus } from '@mimir/common/dto/itemPropTypes';
import type { FolderViewModel } from '@mimir/common/dto/folderModels';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import { SYSTEM_FIELD_ID_QUERYPARAM, TECHNICAL_FIELD_ID_QUERYPARAM, type SearchResultViewModel } from '@mimir/common/search/interfaces';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { PersonResource } from '../pages/person/person-interfaces';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { ItemCreationRequestDto } from '@mimir/common/dto/itemCreationRequestDto';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import eventBus, { EVENTS, showToast, TOAST } from '../eventBus';
import { ItemType } from '@mimir/common/dto/itemTypes';
import { getInjectionIndex } from '@mimir/common/websockets/wsItemInjection';
import { isValidGUID, type VueDeleteValue, type VueSetValue } from '@mimir/common/util/general';
import { handleMdfUpdatesToItem, type MdfUpdateItem } from '@mimir/common/mdf/update';
import { doesItemHavePlaceholderThumbnail, getThumbUrl } from '../util/thumbnail';
import { createPlaceholder, getItem, getItemFields, getItemVersionsHistory, getLatestItemVersion } from '../util/api';
import { generateRouterLink } from '../util/route';
import { logErrorAndShowToast } from '../util/uiError';
import { ItemState } from '@mimir/common/dto/itemState';
import { buildStickySearchString, serializeFilters } from '@mimir/common/filters/Filters';
import { addRouterLink } from '../pages/search/util';
import { features } from '@mimir/web/store';
import type { AxiosError } from 'axios';
import type {
  SearchState,
  SetDefaultDateFieldPayload,
  UpdateSearchHitById,
  ReplaceSearchHitById,
  InjectItemIntoSearchPayload,
  InsertUploadItemIntoSearchPayload,
  SingleItemSearchPayload,
  SearchItemSelectionChange,
  ConsecutiveSelectVariant,
  SearchPageRequest,
  AppendedSearchHits
} from '@mimir/web/model/searchState';
import type { Logger } from '@mimir/common/types/logger';
import { createFinalSearchString } from '@mimir/common/filters/filter-util';
import type { Filters } from '@mimir/common/filters/Filters';
import type { SeparatedFilters } from '@mimir/common/filters/filter-util';
import { invariant } from '@mimir/common/util/invariant';
import { getVersionsSortedByCreatedDateDesc } from '../store/version-sort';
import { VersionState } from '@mimir/common/dto/itemVersionInfo';
import type { DateHelper } from '@mimir/common/util/DateUtil';

const itemsPerPage: Record<ViewType, number> = {
  [ViewType.LIST]: 15,
  [ViewType.GRID]: 30,
  [ViewType.MAP]: 100,
  [ViewType.COMPACT]: 100
};

export const searchState : SearchState = createSearchState();

export function createSearchState(): SearchState {
  const internal = reactive<InternalState>({
    currentItemId: null,
    itemsPerPage: 15,
    isLoadingSearch: false,
    typeCount: [],
    isRecycleBinSearch: false,
    searchForm: {
      searchString: '',
      rawSearchString: '',
      isFuzzy: false,
      atSameTime: false,
      showOnlyDeletedItems: false,
      sortBy: {
        property: SortOptions.ITEM_CREATED_DATE,
        order: SortOrder.DESCENDING
      },
      sortParamsList: [],
      dateTimeSearch: SearchDateOption.ANYTIME,
      defaultDateRangeField: StaticFilterKeywords.DateRange,
      defaultDateRangeFormId: undefined,
      useCustomSearchHook: false,
    },
    lastUsedSortBy: null,
    itemSearch: {
      searchString: '',
      startSearchFrom: 0,
      searchHasMoreHits: false,
      viewType: ViewType.LIST,
      total: 0,
      results: {
        count: 0,
        total: 0,
        from: 0,
        indexOfCurrentLoadedItemInHits: -1,
        typeCount: [],
        hits: [],
        dateHits: {},
        incompleteFolderCount: null,
        truncated: false,
      },
    },
    selectedSearchItems: [],
    lastSearchParams: {},
    customNoResultMessage: null,
    listOfFolderIdsInLastSearchResult: [],
    pathsForNewFolderHitsHasBeenLoaded: false,
    allTypesFilter: false,
    pendingPageRequests: 0,
    removedHitIds: new Set<string>(),
    listId: 0,
  });

  function startNewList(): void {
    internal.removedHitIds.clear();
    internal.listId++;
  }

  const ops = {
    // From the store mutations
    setCurrentItemId(itemId: string | null) {
      if (!internal.itemSearch.results.hits.find((item) => item.id === itemId)) {
        internal.currentItemId = null;
      } else {
        internal.currentItemId = itemId;
      }
    },
    changeSelection(
      change: SearchItemSelectionChange,
      tryPreviewItem: (item: SearchResultHit, timeInMsHint: number | null, commitSelection: () => void) => boolean,
    ): void {
      const changeMode = change.mode.type;
      switch (changeMode) {
        case 'consecutive_select':
          consecutiveSearchItemSelection(
            { searchState },
            change.item,
            change.timeInMsHint,
            change.mode.variant,
            tryPreviewItem
          );
          return;
        case 'single_select':
        case 'multi_select':
          toggleSearchItemSelection(
            { searchState },
            change.item,
            change.timeInMsHint,
            changeMode === 'multi_select',
            tryPreviewItem
          );
          return;
        default:
          changeMode satisfies never;
      }
    },
    isSelectedSearchItem(item: SearchResultHit): boolean {
      return internal.selectedSearchItems.some(selectedItem => selectedItem.id === item.id);
    },
    setCustomNoResultMessage(payload: string | null): void {
      internal.customNoResultMessage = payload;
    },
    setIndexOfCurrentLoadedItemInHits(payload: number): void {
      internal.itemSearch.results.indexOfCurrentLoadedItemInHits = payload;
    },
    setIsRecycleBinSearch(payload: boolean): void {
      internal.isRecycleBinSearch = payload;
    },
    resetSearch(): void {
      startNewList();
      internal.itemSearch.startSearchFrom = 0;
      internal.itemSearch.total = 0;
      internal.itemSearch.results = {
        count: 0,
        total: 0,
        from: 0,
        indexOfCurrentLoadedItemInHits: -1,
        typeCount: [],
        hits: [],
        dateHits: {},
        incompleteFolderCount: null,
        truncated: false,
      };
      internal.typeCount = [];
    },
    clearSearchString(): void {
      internal.searchForm.rawSearchString = '';
      internal.searchForm.searchString = '';
    },
    setTypeCount(payload: SearchCategoryCount[]): void {
      internal.typeCount = payload;
    },
    setRawSearchString(value: string): void {
      internal.searchForm.rawSearchString = value;
    },
    setSearchString(value: string): void {
      internal.searchForm.searchString = value;
    },
    setItemSearch(value: ItemSearchState): void {
      startNewList();
      internal.itemSearch = value;
    },
    setItemsPerPage(payload: number): void {
      internal.itemsPerPage = payload;
    },
    setSameTimeFilter(payload: boolean): void {
      internal.searchForm.atSameTime = payload;
    },
    setViewSpecificDetails(): void {
      internal.itemsPerPage = itemsPerPage[internal.itemSearch.viewType ?? ViewType.LIST];
    },
    setIsLoadingSearch(payload: boolean): void {
      internal.isLoadingSearch = payload;
    },
    setSortParamsList(payload: SortParameters[]): void {
      internal.searchForm.sortParamsList = payload;
    },
    setSearchFormSortParams(payload: { params: SortParameters, doNotChangeLevel?: boolean }): void {
      const { params, doNotChangeLevel } = payload;
      if (internal.itemSearch.viewType === ViewType.COMPACT) {
        const sortParamsList = internal.searchForm.sortParamsList;
        if (!sortParamsList) {
          // no previous list. make a new one.
          internal.searchForm.sortParamsList = [params];
        } else {
          // find if the old list already has this sort prop
          const previousIndex = sortParamsList.findIndex(sortParams => sortParams.property === params.property);
          if (previousIndex >= 0) {
            // if already there, remove it
            sortParamsList.splice(previousIndex, 1);
          }
          // At this point the param is removed from sortParamsList, if present in old list.
          // Now if order property is missing we should return. From header-context-menu user can clear column order.
          if (params.order === undefined || params.order === '') {
            return;
          }
          // should not change sort level when user only change sort order and the param has previousIndex > 0.
          if (doNotChangeLevel && previousIndex > 0) {
            sortParamsList.push(params);
          } else {
            // add it to the top as the most significant sort prop
            sortParamsList.unshift(params);
          }

          // if list grows more than max, truncate it to keep most recent sort props
          const maxSortCols = 2;
          if (sortParamsList.length > maxSortCols) {
            sortParamsList.length = maxSortCols;
          }
        }

        // do not modify the old sortBy prop
        return;
      }
      internal.searchForm.sortBy = params;
    },
    setSearchDate(payload: SearchDateOption): void {
      internal.searchForm.dateTimeSearch = payload;
    },
    setDefaultDateRangeField(payload: SetDefaultDateFieldPayload | undefined): void {
      if (payload?.fieldIdOrGuid) internal.searchForm.defaultDateRangeField = payload.fieldIdOrGuid;
      if (payload?.formId) internal.searchForm.defaultDateRangeFormId = payload.formId;
    },
    setLastUsedSortBy(payload: SortParameters | null): void {
      internal.lastUsedSortBy = payload;
    },
    lastSearchParams(payload: Record<string, string>): void {
      internal.lastSearchParams = payload;
    },
    setRestoreMediaCompletedInSearchResult(payload: { itemId: string, highRes: string | undefined, mediaOnlineUntil: string | undefined }): void {
      for (const item of internal.itemSearch.results.hits) {
        if (item && item.archiveInfo && ((item.id === payload.itemId)
        || (item.virtualClipInfo && item.virtualClipInfo.masterClipId === payload.itemId))) {
          // Not setting highRes as SearchResultHit does not need the highRes link
          Vue.set(item.archiveInfo, 'restoreStatus', ArchiveRestoreStatus.Completed);
          Vue.set(item.archiveInfo, 'mediaOnlineUntil', payload.mediaOnlineUntil);
        }
      }
    },
    setMediaRestoreStatusInSearchResult(payload: { itemId: string, restoreStatus: ArchiveRestoreStatus }): void {
      for (const item of internal.itemSearch.results.hits) {
        if (item && item.archiveInfo && ((item.id === payload.itemId)
          || (item.virtualClipInfo && item.virtualClipInfo.masterClipId === payload.itemId))) {
          Vue.set(item.archiveInfo, 'restoreStatus', payload.restoreStatus);
        }
      }
    },
    updateFolderInSearch(payload: { id: string, name?: string, owner?: string }): void {
      const { id, name, owner } = payload;
      if (internal.itemSearch.results) {
        const hit = internal.itemSearch.results.hits.find(item => item.id === id);
        if (hit) {
          if (name !== undefined) hit.name = name;
          if (owner !== undefined) hit.owner = owner;
        }
      }
    },
    injectFolderIntoSearchResult(payload: SearchResultHit): void {
      internal.removedHitIds.delete(payload.id);
      if (internal.itemSearch.results) {
        if (internal.itemSearch.results.hits.some(hit => hit.id === payload.id)) {
          showToast('Folder already present in the current folder', TOAST.INFO);
          return;
        }

        payload.itemType = ItemType.FOLDER;

        const hasFolders = internal.itemSearch.results.hits.some(hit => hit.itemType === ItemType.FOLDER);
        const hasNonFolders = internal.itemSearch.results.hits.some(hit => hit.itemType !== ItemType.FOLDER);

        // Can safely insert at the start.
        if (!hasFolders) {
          internal.itemSearch.results.hits.unshift(payload);
          return;
        }

        if (!hasNonFolders) {
          const unsorted = [...internal.itemSearch.results.hits];
          unsorted.push(payload);
          internal.itemSearch.results.hits = sortBy(unsorted, hit => hit.name);
        } else {
          const index = internal.itemSearch.results.hits.findIndex(hit => hit.itemType !== ItemType.FOLDER);
          let unsorted = internal.itemSearch.results.hits.splice(0, index);
          unsorted.push(payload);
          unsorted = sortBy(unsorted, hit => hit.name);
          internal.itemSearch.results.hits = [...unsorted, ...internal.itemSearch.results.hits];
        }
      }
    },
    injectItemsIntoSearchResult(payload: {items: SearchResultHit[], mdfs?: MetaDataSchema[], ignoreSorting?: boolean}): void {
      if (!internal.itemSearch.results) return;
      const { items, ignoreSorting, mdfs = [] } = payload;
      for (const item of items) {
        internal.removedHitIds.delete(item.id);
      }

      let firstNonFolderIndex = 0;
      for (let i = 0; i < internal.itemSearch.results.hits.length; i++) {
        if (internal.itemSearch.results.hits[i].itemType !== ItemType.FOLDER) {
          firstNonFolderIndex = i;
          break;
        }
      }

      // For version search, we need to inject versions in the order they are received. Versions are not fetched through search api.
      // We always ignore websocket item creation message for version view. So we do not have to worry about memory bloating.
      if (ignoreSorting) {
        internal.itemSearch.results.hits.splice(firstNonFolderIndex, 0, ...items);
        internal.itemSearch.results.total += items.length;
        internal.itemSearch.total += items.length;
        return;
      }

      const sortParamList = internal.searchForm.sortParamsList as SortParameters[];
      const sortFieldsInfo = extractSortFieldsInfo(sortParamList, mdfs);
      const isCompactView = internal.itemSearch.viewType === ViewType.COMPACT;
      const sorParams: SortParameters[] = isCompactView ? sortParamList : [internal.searchForm.sortBy as SortParameters];
      const isAllItemsLoaded = internal.itemSearch.results.hits.length === internal.itemSearch.total;

      const itemsToBeInjected: {item: SearchResultHit, index: number}[] = [];
      for (const item of items) {
        const index = getInjectionIndex(item, sorParams, internal.itemSearch.results.hits, isAllItemsLoaded, isCompactView, sortFieldsInfo);
        if (index !== null) {
          itemsToBeInjected.push({ item, index });
        }
      }
      if (itemsToBeInjected.length === 0) return;
      itemsToBeInjected.sort((a, b) => b.index - a.index);

      // Unnecessary memory bloating can happen when search view is left alone for a long time
      // while new items keep getting injected through WebSocket events. To prevent this, we
      // are evicting the bottom results from the currently loaded array before adding the new ones.
      // The threshold is sized for views that mount a DOM row per loaded hit. A windowed view mounts
      // only the visible rows, leaving just the hit objects in memory, so once no view mounts every
      // hit the threshold can rise until eviction only bounds a view left open for a long time.
      // A pending page continues from the current tail, so evicting it would leave a gap before that
      // page; the list instead grows by the injections made during the request.
      const nonFolderItemsLength = internal.itemSearch.results.hits.length - firstNonFolderIndex;
      let evictedCount = 0;
      if (
        internal.pendingPageRequests === 0
        && internal.itemSearch.results.hits.length > 100
        && nonFolderItemsLength >= itemsToBeInjected.length
      ) {
        evictedCount = itemsToBeInjected.length;
        internal.itemSearch.results.hits.length -= evictedCount;
        internal.itemSearch.results.total -= evictedCount;
        internal.itemSearch.searchHasMoreHits = true;
      }

      for (const { item, index } of itemsToBeInjected) {
        internal.itemSearch.results.hits.splice(index, 0, item);
      }

      // Injected items do not advance the cursor, although they will shift the server's offsets:
      // the server may index them before or after the next page request, and a cursor that runs
      // ahead skips hits, while one that lags only repeats hits that `appendSearchHits` drops.
      // Evicted hits were the last loaded ones, so the cursor steps back to fetch them again.
      internal.itemSearch.startSearchFrom = Math.max(0, internal.itemSearch.startSearchFrom - evictedCount);

      internal.itemSearch.results.total += itemsToBeInjected.length;
      internal.itemSearch.total += itemsToBeInjected.length;
    },
    removeFromSearchById(id: string): void {
      internal.removedHitIds.add(id);
      if (internal.itemSearch.results) {
        const index = internal.itemSearch.results.hits.findIndex(item => item.id === id);
        if (index >= 0) {
          internal.itemSearch.results.hits.splice(index, 1);
          internal.itemSearch.results.total--;
          internal.itemSearch.total--;
        }
      }
    },
    updateSearchHitById(payload: UpdateSearchHitById): void {
      const { id, update, retrievedFields } = payload;
      /*
        `payload.retrievedFields` contains a list of keys fetched from the server.
        Since `undefined` values are not transmitted over the API, we combine the
        fetched keys with those available in the `payload.update` object.

        This ensures that the client state is updated with the most recent data
        while correctly handling stale state updates.
      */
      const uniqueKeys = new Set([...Object.keys(update), ...(retrievedFields ?? [])]);
      const keysToIterateOver = Array.from(uniqueKeys) as (keyof SearchResultHit)[];

      const searchHit = internal.itemSearch.results.hits.find(hit => hit.id === id);
      if (!searchHit) {
        return;
      }
      const doesUpdateHaveMedia = keysToIterateOver.find(el => el === 'media');
      const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
      const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
      for (const prop of keysToIterateOver) {
        if (prop === 'metadata' && update.metadata !== undefined) {
          let metadataVersion = update.metadataVersion;
          if (metadataVersion == undefined) {
            console.warn('updateSearchHitById: metadata update without metadataVersion');
            metadataVersion = 0;
          }
          handleMdfUpdatesToItem(searchHit, update.metadata, metadataVersion, vueSet, vueDeleter);
          continue;
        }
        // in shouldPollForUpdates we use 'media' property as the source of truth if kelda live item's
        // thumbnail is available. but there is a slight window where thumbnail is available but item
        // doesn't yet have the 'media' property. if a user reloads the folder during this time period
        // the thumbnail flashes black. this check addresses this problem by checking if the item already
        // has a valid thumbnail url
        if (
          prop === 'thumbnail' &&
          doesUpdateHaveMedia &&
          !doesItemHavePlaceholderThumbnail(searchHit as ClientItem)
        ) {
          continue;
        }

        Vue.set(searchHit, prop, update[prop]);
      }
    },
    replaceSearchHitById(payload: ReplaceSearchHitById): void {
      const { newItem, itemIdToReplace } = payload;
      internal.removedHitIds.add(itemIdToReplace);
      internal.removedHitIds.delete(newItem.id);
      const hits = internal.itemSearch.results.hits;
      const index = hits.findIndex(hit => hit.id === itemIdToReplace);
      if (index < 0) {
        return;
      }
      const wasSelected = hits[index].selected;
      Vue.set(hits, index, newItem);
      if (wasSelected)  {
        internal.selectedSearchItems.push(newItem);
        Vue.set(newItem, 'selected', true);
      }
    },
    addToSelectedSearchItems(item: SearchResultHit | null | undefined): void {
      if (!item) return;
      if (!internal.selectedSearchItems.find(selectedItem => selectedItem.id === item.id)) {
        internal.selectedSearchItems.push(item);
        Vue.set(item, 'selected', true);
      }
    },
    removeIdsFromSearchResult(payload: { onlyFolders?: boolean, idsToRemove: string[] }): void {
      const { onlyFolders, idsToRemove } = payload;
      const hits = internal.itemSearch.results.hits;
      for (const id of idsToRemove) {
        const index = hits.findIndex(hit => hit.id === id);
        if (index >= 0) {
          const hit = hits[index];
          if (onlyFolders && hit.itemType !== ItemType.FOLDER) {
            continue;
          }
          hits.splice(index, 1);
        }
        internal.removedHitIds.add(id);
      }
    },
    removeFromSelectedSearchItems(item: SearchResultHit | null | undefined): void {
      if (!item) return;

      const idx = internal.selectedSearchItems.findIndex(selectedItem => selectedItem.id === item.id);
      if (idx === -1) return;

      internal.selectedSearchItems.splice(idx, 1);
      Vue.set(item, 'selected', false);
    },
    clearSelectedSearchItems(): void {
      if (!internal.selectedSearchItems.length) return;
      internal.selectedSearchItems.map((item: SearchResultHit) => {
        Vue.set(item, 'selected', false);
      });
      internal.selectedSearchItems = [];
    },
    resetSearchResultArray(): void {
      startNewList();
      internal.itemSearch.results.count = 0;
      internal.itemSearch.results.hits = [];
    },
    updateMetadataOfSearchHitItem(payload: {id: string, metadata: ItemMetaData, metadataVersion: number }): void {
      const { id, metadata, metadataVersion } = payload;
      const vueSet = (v: VueSetValue) => Vue.set(v.object, v.key, v.value);
      const vueDeleter = (v: VueDeleteValue) => Vue.delete(v.object, v.key);
      const itemFromSearch: MdfUpdateItem | undefined = internal.itemSearch.results.hits.find(hit => hit.id === id);
      if (itemFromSearch) {
        handleMdfUpdatesToItem(itemFromSearch, metadata, metadataVersion, vueSet, vueDeleter);
      }
    },
    beginPageRequest(): SearchPageRequest {
      internal.pendingPageRequests++;
      const from = internal.itemSearch.startSearchFrom;
      // The cursor lags the list's end by the injections it cannot count as indexed, so the page
      // extends by that lag to reach a full page past the listed hits whether they are or not.
      const lag = Math.max(0, internal.itemSearch.results.hits.length - from);
      return { from, size: internal.itemsPerPage + lag, listId: internal.listId };
    },
    endPageRequest(): void {
      invariant(internal.pendingPageRequests > 0, 'endPageRequest without a pending beginPageRequest');
      internal.pendingPageRequests--;
    },
    isPageRequestForCurrentList(pageRequest: SearchPageRequest): boolean {
      return pageRequest.listId === internal.listId;
    },
    /** The hits of a continuation page that appending it now would add to the list. */
    hitsToAdd(hits: SearchResultHit[]): SearchResultHit[] {
      return mergePageIntoListedHits(internal.itemSearch.results.hits, hits, internal.removedHitIds).added;
    },
    /**
     * Append search hits to itemSearch, and update isRecycleBinSearch and
     * typeCount. Hits that are already listed, or were removed from the list,
     * are dropped.
     */
    appendSearchHits(payload: SearchResultViewModel): AppendedSearchHits {
      const searchState = internal.itemSearch;
      searchState.total = payload.total;
      // Follows the server's offsets, so hits dropped below as already listed still count.
      searchState.startSearchFrom = payload.from + payload.hits.length;
      searchState.searchHasMoreHits = searchState.startSearchFrom < payload.total;

      let addedHits: SearchResultHit[];
      let appendedCount: number;
      if (searchState.results && searchState.results.hits) {
        const merged = mergePageIntoListedHits(searchState.results.hits, payload.hits, internal.removedHitIds);
        searchState.results.hits = merged.hits;
        addedHits = merged.added;
        appendedCount = merged.appendedCount;
      } else {
        addedHits = payload.hits;
        appendedCount = payload.hits.length;
        searchState.results = payload;
      }
      searchState.results.truncated = payload.truncated;

      addedHits.forEach(hit => {
        if (hit.itemType === ItemType.FOLDER) internal.listOfFolderIdsInLastSearchResult.push(hit.id);
      });

      if (searchState.results.dateHits && !isEqual(searchState.results.dateHits, payload.dateHits)) {
        searchState.results.dateHits = payload.dateHits;
      }

      internal.isRecycleBinSearch = payload.isRecycleBin;
      if (!payload.isRecycleBin) {
        internal.typeCount = payload.typeCount;
      }

      return { added: addedHits, appendedCount };
    },
    setPathsForNewFolderHitsHasBeenLoaded(val: boolean): void {
      internal.pathsForNewFolderHitsHasBeenLoaded = val;
    },
    setListOfFolderIdsInLastSearchResult(payload: string[]): void {
      internal.listOfFolderIdsInLastSearchResult = payload;
    },
    setAllTypesFilter(payload: boolean): void {
      internal.allTypesFilter = payload;
    },


    // From the store actions

    /**
     * There was an action in the old vuex store module called
     * setDefaultDateRangeField, but this was mutating a bunch of other stuff
     * in the global store so it has been moved to 'setDefaultDateRangeField.ts'
     */

    /**
     * There was an action called resetSearch() here. This has been moved to
     * its own file 'resetSearchState.ts'
     */

    async singleItemSearch(payload: SingleItemSearchPayload): Promise<void> {
      const {
        itemId,
        currentFolder,
        loadItem,
      } = payload;

      // First determine if we already did this search, to avoid unnecessary searches when user
      // swaps between view types for example.
      if (internal.itemSearch.results.hits.length === 1 && internal.itemSearch.results.hits[0].id === itemId) {
        return;
      }

      this.resetSearch();
      this.setIsLoadingSearch(true);
      this.clearSelectedSearchItems();
      try {
        const item = await getItem(itemId);
        try {
          const latestVersion = await getLatestItemVersion(itemId);
          if (latestVersion.id !== itemId) {
            item.latestVersionId = latestVersion.id;
          }
        } catch(err) {
          console.warn(`Could not get latest version of item ${itemId}`, err);
        }
        item.routerLink = generateRouterLink(item.id, internal.lastSearchParams, item.itemType);
        this.injectItemIntoSearchResult({ item, shouldUpdateExisting: false, currentFolder });
        loadItem(item);
        this.addToSelectedSearchItems(item);
      } catch (err: any) {
        if (err?.response?.data?.error?.statusCode === 404) {
          this.setCustomNoResultMessage(`Item with id '${itemId}' could not be found.`);
        } else {
          logErrorAndShowToast(err?.response?.data?.error?.message || 'Could not load item search result', err, 'ex in singleItemSearch');
        }
      } finally {
        this.setIsLoadingSearch(false);
      }
    },
    // TODO: All places that call this must call the following code:
    //if (isInElectron(payload.applicationContext)) {
    //  return loadFolderPathForItem(payload.isProxyMode, payload.applicationContext, dispatch, payload.item);
    //}
    injectItemIntoSearchResult(payload: InjectItemIntoSearchPayload): void {
      const { item, shouldUpdateExisting, currentFolder } = payload;
      if (internal.itemSearch.results) {

        if (!doesItemBelongInCurrentFolder(item, currentFolder)) return;
        internal.removedHitIds.delete(item.id);

        let firstNonFolderIndex = -1;
        for (let i = 0; i < internal.itemSearch.results.hits.length; i++) {
          const hit = internal.itemSearch.results.hits[i];
          if (hit.itemType !== ItemType.FOLDER && firstNonFolderIndex === -1) {
            firstNonFolderIndex = i;
          }
          // If the item already exists - don't inject.
          if (hit.id === item.id) {
            if (shouldUpdateExisting) Vue.set(internal.itemSearch.results.hits, i, { ...hit, ...item });
            return;
          }
        }

        if (firstNonFolderIndex < 0) {
          internal.itemSearch.results.hits.push(item);
        } else {
          internal.itemSearch.results.hits.splice(firstNonFolderIndex, 0, item);
        }
        internal.itemSearch.results.total++;
        internal.itemSearch.total++;
      }
    },
    insertUploadItemIntoSearch(payload: InsertUploadItemIntoSearchPayload): void {
      const {
        uploadItem,
        addRouterLink,
        currentFolder,
        loadFolderPathForItem,
      } = payload;

      uploadItem.addCreatedItemCallback(() => {
        const hits = internal.itemSearch.results?.hits;
        if (hits) {
          if (!doesItemBelongInCurrentFolder(uploadItem, currentFolder)) return;

          let index = -1;
          let existingItem: SearchResultHit | undefined = undefined;
          // First check if we've optimistically inserted a placeholder with a fake id.
          const fakeItemIndex = hits.findIndex(hit => hit.id === uploadItem.localId);
          if (fakeItemIndex >= 0) {
            index = fakeItemIndex;
          } else {
            // Otherwise check if we have one with a proper id we need to replace.
            const realItemIndex = hits.findIndex(hit => hit.id === uploadItem.id);
            if (realItemIndex >= 0) {
              existingItem = hits[realItemIndex];
              index = realItemIndex;
            }
          }

          const searchHit: ClientItem = {
            vttUrl: '',
            itemType: uploadItem.derivedItemType,
            name: uploadItem.title,
            metadata: uploadItem.metadata as ItemMetaData,
            id: uploadItem.id,
            thumbnail: existingItem?.thumbnail ?? '',
            operationsLink: '',
            originalFileName: uploadItem.file.name,
            proxy: null,
            itemState: uploadItem.itemState !== null ? uploadItem.itemState : ItemState.NEW,
            createdOn: new Date().toISOString(),
            mediaDuration: 0,
            routerLink: addRouterLink ? generateRouterLink(uploadItem.id, internal.lastSearchParams, uploadItem.derivedItemType) : undefined,
            folderParents: uploadItem.folderParents ?? [],
            createdBy: uploadItem.createdBy,
            languageCode: uploadItem.itemAnalyticsProperties?.languageCode,
            visibleTo: uploadItem.visibleTo,
            updatableBy: uploadItem.updatableBy,
          };


          if (loadFolderPathForItem) {
            loadFolderPathForItem(searchHit);
          }

          // Object was to be replaced. Update the selection references
          if (index >= 0) {
            const updatePayload: UpdateSearchHitById = {
              id: uploadItem.id,
              update: searchHit,
            };
            this.updateSearchHitById({
              id: uploadItem.id,
              update: updatePayload,
            });
          } else {
            return this.injectItemIntoSearchResult({
              item: searchHit,
              shouldUpdateExisting: false,
              currentFolder: currentFolder,
            });
          }
        }
      });

    },
    async updateSearchHitThumbnailById(payload: string): Promise<void> {
      const searchHit = internal.itemSearch.results.hits.find(hit => hit.id === payload);
      if (!searchHit) return;
      const obj = await getItemFields(payload, ['thumbnail']);
      searchHit.thumbnail = obj.thumbnail;
    },
    async doStickySearch(payload: string, clearFilters: (clearSticky: boolean) => void): Promise<void> {
      const newSearchString = buildStickySearchString(payload, !features.isFeatureEnabled('disableSearchFilterParsingV2'));
      this.setSearchString(newSearchString);

      clearFilters(true);

      eventBus.emit(EVENTS.DO_SEARCH);
    },
    updatePersonWithRouterLink(person: PersonResource): void {
      addRouterLink([person], {});
    },
    async createPlaceHolderAndSearchHit(payload: {
      item: Partial<ItemCreationRequestDto>,
      inject: boolean,
      currentFolder: FolderViewModel | null,
    }): Promise<SearchResultHit> {
      const { item, inject, currentFolder } = payload;
      return new Promise((resolve, reject) => {
        createPlaceholder(item).then(response => {
          showToast('New item created', TOAST.SUCCESS);
          const searchHit: ClientItem = {
            name: '',
            itemType: ItemType.FILE,
            mediaDuration: 0,
            thumbnail: getThumbUrl('', response.itemType),
            proxy: null,
            ...response,
            itemState: ItemState.NEW,
            routerLink: generateRouterLink(response.id, internal.lastSearchParams, ItemType.FILE),
          };

          if (inject) {
            this.injectItemIntoSearchResult({ item: searchHit, shouldUpdateExisting: false, currentFolder });
          }

          resolve(searchHit);
        }).catch(err => {
          const errorMessageFromServer = (err as Partial<AxiosError>)?.response?.data?.error?.message;
          showToast(errorMessageFromServer ?? 'Something went wrong creating new item', TOAST.ERROR);
          reject(err);
        });
      });
    },
    injectProgressInfo({itemId, progressInfo}: { itemId: string, progressInfo?: ProgressInfo}): void {
      for (const hit of internal.itemSearch.results.hits) {
        if (hit.id === itemId) {
          Vue.set(hit, 'progressInfo', progressInfo ?? { status: 'Preparing ..' });
          break;
        }
      }
    },
    updateFromQueryParams(
      logger: Logger,
      filters: Filters,
      mdfs: MetaDataSchema[],
      separatedFilters: SeparatedFilters,
      queryParams: any
    ): void {
      const { searchString, viewType, isFuzzy, atSameTime, sortParamsList, defaultDateRangeField, defaultDateRangeFormId, useCustomSearchHook, allTypes } = queryParams;

      this.setDefaultDateRangeField({ fieldIdOrGuid: defaultDateRangeField, formId: defaultDateRangeFormId });
      if (allTypes === 'true' || allTypes === 'false') {
        this.setAllTypesFilter(allTypes === 'true');
      }

      // Any unrecognized filter at this point will be part of the remaining search string.
      const finalSearchString = createFinalSearchString(separatedFilters);

      const serializedFilterString = serializeFilters(filters, mdfs);
      internal.searchForm.rawSearchString = searchString?.length > 0 ? searchString : serializedFilterString;
      internal.itemSearch.searchString = internal.searchForm.searchString = finalSearchString;

      const folderSearchText = filters.defaultFilters['filter:folder'].getValue();
      const isFolderOnly = filters.defaultFilters['filter:itemType'].selectedCategories.length === 1 && (filters.defaultFilters['filter:itemType'].getValue() === ItemType.FOLDER);

      // If we have a folder search text we should clear the folder text filter
      // and set the search string to the folder search text.
      if (isFolderOnly && folderSearchText) {
        internal.itemSearch.searchString = internal.searchForm.searchString = folderSearchText;
        filters.defaultFilters['filter:folder'].clearValue();
      }

      internal.itemSearch.viewType = (Object.values(ViewType).includes(viewType)) ? viewType as ViewType : ViewType.LIST;

      if ('isFuzzy' in queryParams) {
        internal.searchForm.isFuzzy = typeof isFuzzy === 'boolean' ? isFuzzy : isFuzzy === 'true';
      }

      if ('atSameTime' in queryParams) {
        internal.searchForm.atSameTime = typeof atSameTime === 'boolean' ? atSameTime : atSameTime === 'true';
      }

      if ('useCustomSearchHook' in queryParams) {
        internal.searchForm.useCustomSearchHook = typeof useCustomSearchHook === 'boolean' ? useCustomSearchHook : useCustomSearchHook === 'true';
      }
      if (sortParamsList) {
        try {
          const sortParamsListParsed = JSON.parse(sortParamsList);
          this.setSortParamsList(sortParamsListParsed);
        } catch (err) {
          logger.error(`Could not parse sortParamsList: ${sortParamsList}`);
        }
      }

      // if dateRange filter is set and we have dateTimeSearch is set as Anytime then it can only
      // happen during page reload and hence setting custom option here.
      if (filters.dateFilter.hasValue && internal.searchForm.dateTimeSearch === SearchDateOption.ANYTIME) {
        this.setSearchDate(SearchDateOption.CUSTOM);
      }
    },
    toggleFuzzySearch(): void {
      internal.searchForm.isFuzzy = !internal.searchForm.isFuzzy;
    },
    toggleCustomSearchHook(): void {
      internal.searchForm.useCustomSearchHook = !internal.searchForm.useCustomSearchHook;
    },
    disableCustomSearchHook(): void {
      internal.searchForm.useCustomSearchHook = false;
    },
    cloneSearchParams(): SearchParams {
      return cloneDeep(internal.searchForm);
    },
    async populateSearchResultsWithVersionsForItem(services: { dateHelper: DateHelper }, itemId: string): Promise<void> {
      if (!isValidGUID(itemId)) {
        return;
      }
      // TODO: This function does a lot of small mutations of the search
      // state. Should it instead be a function in the search state?
      this.resetSearch();
      this.setIsLoadingSearch(true);
      this.clearSelectedSearchItems();
      try {
        const versionsHistoryDto = await getItemVersionsHistory(itemId, true);
        versionsHistoryDto.versions[0].isLatestVersion = true;
        const itemVersionsHistorySortedByDateDesc = getVersionsSortedByCreatedDateDesc(versionsHistoryDto.versions);
        const versionsToInject = itemVersionsHistorySortedByDateDesc.map((v, i) => {
          const versionName = v.name || services.dateHelper.format(v.versionCreatedOn, { format: 'DayMonthYearHourMinutes' });
          v.versionName = versionName + ' (v' + (versionsHistoryDto.versions.length - i) + ')';
          return v;
        });
        searchState.ops.injectItemsIntoSearchResult({
          // TODO: While converting the search module to a global state object it revealed that
          // we are breaking type safety here.
          items: versionsToInject.filter(v => v.versionState !== VersionState.DELETED) as unknown as SearchResultHit[],
          ignoreSorting: true
        });
        const versionToShowSelected = versionsToInject.find(s => s.id === itemId && s.versionState !== VersionState.DELETED);
        if (versionToShowSelected) {
          // TODO: While converting the search module to a global state object it revealed that
          // we are breaking type safety here.
          searchState.ops.addToSelectedSearchItems(versionToShowSelected as unknown as SearchResultHit | null);
        }
      } catch (err: any) {
        if (err?.response?.data?.error?.statusCode === 404) {
          searchState.ops.setCustomNoResultMessage(`Item with id '${itemId}' could not be found.`);
        } else {
          logErrorAndShowToast('Could not get item version history', err);
        }
      } finally {
        searchState.ops.setIsLoadingSearch(false);
      }
    }
  };

  return {
    reactive: internal,
    ops
  };
}

interface InternalState {
  currentItemId: string | null,
  itemsPerPage: number,
  isLoadingSearch: boolean,
  searchForm: SearchParams,
  itemSearch: ItemSearchState,
  typeCount: SearchCategoryCount[],
  selectedSearchItems: SearchResultHit[],
  isRecycleBinSearch: boolean;

  // Used to keep track of the last search, mainly to generate router links on items so the user
  // can always return to the last search performed.
  lastSearchParams: Record<string, string>;
  customNoResultMessage: string | null;
  listOfFolderIdsInLastSearchResult: string[];
  pathsForNewFolderHitsHasBeenLoaded: boolean;
  lastUsedSortBy: SortParameters | null;
  allTypesFilter: boolean;
  // Continuation page requests between `beginPageRequest` and `endPageRequest`.
  pendingPageRequests: number;
  // Hits the client took out of the list since it was last reset. The index keeps returning them
  // until the change behind the removal is indexed, so pages leave them out.
  removedHitIds: Set<string>;
  // Changes whenever a new search replaces the list, so a page requested for the old list is not appended to it.
  listId: number;
}

interface MergedPage {
  hits: SearchResultHit[];
  added: SearchResultHit[];
  appendedCount: number;
}

// A page overlaps the list only when the server's results and the list have drifted apart; otherwise
// it is appended as is. The page is in server order: listed and removed hits are dropped, and an
// unlisted hit goes right before the next listed hit in the page, or past the list's end if none follows.
function mergePageIntoListedHits(
  listed: readonly SearchResultHit[],
  page: readonly SearchResultHit[],
  removedIds: ReadonlySet<string>,
): MergedPage {
  const listedIds = new Set(listed.map(hit => hit.id));
  const insertBefore = new Map<string, SearchResultHit[]>();
  const added: SearchResultHit[] = [];
  let unplaced: SearchResultHit[] = [];
  for (const hit of page) {
    if (removedIds.has(hit.id)) {
      continue;
    }
    if (!listedIds.has(hit.id)) {
      unplaced.push(hit);
      added.push(hit);
    } else if (unplaced.length > 0) {
      insertBefore.set(hit.id, unplaced);
      unplaced = [];
    }
  }

  const hits: SearchResultHit[] = [];
  for (const hit of listed) {
    const inserted = insertBefore.get(hit.id);
    if (inserted !== undefined) {
      hits.push(...inserted);
    }
    hits.push(hit);
  }
  hits.push(...unplaced);
  return { hits, added, appendedCount: unplaced.length };
}

const extractSortFieldsInfo = (params: SortParameters[], mdfs: MetaDataSchema[]) => {
  const sortFieldsInfo: Record<string, { id: string }> = {};
  for (const param of params) {
    // If mdfSource is a regular schema then we need to extract the id. So that we can extract
    // the field value from websocket item metadata.
    if (param?.fieldId && param.mdfSource && param.mdfSource !== SYSTEM_FIELD_ID_QUERYPARAM && param.mdfSource !== TECHNICAL_FIELD_ID_QUERYPARAM) {
      const mdfToUse = mdfs.find(mdf => mdf.label === param.mdfSource);
      if (!mdfToUse) continue;
      if (mdfToUse.fields) {
        const field = mdfToUse.fields.find(f => f.fieldId === param.fieldId);
        if (field) {
          sortFieldsInfo[param.fieldId] = {
            id: field.id
          };
        }
      }
    }
  }
  return sortFieldsInfo;
};

/**
 * Checks if the given item is a child of the currently loaded folder.
 * Returns true if:
 * - There is no current folder loaded
 * - Item doesn't have any folder parents
 * - Item is a child of the currently loaded folder
 *
 * TODO: This one does not belong here. It has nothing to do with the SearchState
 */
export const doesItemBelongInCurrentFolder = (item: { folderParents: string[] | null | undefined }, currentFolder: FolderViewModel | null) => {
  const uploadItemParents = item.folderParents ?? [];
  if (currentFolder && uploadItemParents.length > 0 && !(uploadItemParents.includes(currentFolder.id))) {
    return false;
  }
  return true;
};

function consecutiveSearchItemSelection(
  services: {
    searchState: SearchState,
  },
  item: SearchResultHit,
  timeInMsHint: number | null,
  variant: ConsecutiveSelectVariant,
  tryPreviewItem: (item: SearchResultHit, timeInMsHint: number | null, commitSelection: () => void) => boolean,
) {
  const { searchState } = services;
  const itemSearch = searchState.reactive.itemSearch;
  const items = itemSearch.results.hits;

  const targetIndex = items.findIndex(hit => hit.id === item.id);
  const currentIndex = items.findIndex(hit => hit.id === searchState.reactive.currentItemId) ?? targetIndex;

  if ((items.length === 0) || (currentIndex === -1)) {
    toggleSearchItemSelection(services, item, timeInMsHint, false, tryPreviewItem);
    return;
  }

  // Find the first index and last index of the selection range around
  // the currently selected item.
  let firstIndex = currentIndex;
  while ((firstIndex > 0) && (items[firstIndex - 1].selected)) {
    firstIndex --;
  }

  let lastIndex = currentIndex;
  while ((lastIndex < items.length - 1) && (items[lastIndex + 1].selected)) {
    lastIndex ++;
  }


  switch (variant) {
    case 'expand': {
      const commitSelection = () => {
        searchState.ops.setCurrentItemId(item.id);

        const minIndex = Math.min(firstIndex, targetIndex);
        const maxIndex = Math.max(lastIndex, targetIndex);
        for (let i = minIndex; i <= maxIndex; i++) {
          searchState.ops.addToSelectedSearchItems(items[i]);
        }
      };


      if (!tryPreviewItem(item, timeInMsHint, commitSelection)) {
        // Unsaved changes etc prevented preview
        return;
      }

      commitSelection();
      break;
    }
    case 'move_end': {
      const commitSelection = () => {
        searchState.ops.setCurrentItemId(item.id);

        const distanceToFirst = Math.abs(firstIndex - currentIndex);
        const distanceToLast = Math.abs(lastIndex - currentIndex);

        searchState.ops.clearSelectedSearchItems();
        if (firstIndex == lastIndex) {
          for (let i = Math.min(targetIndex, firstIndex); i <= Math.max(targetIndex, lastIndex); i++) {
            searchState.ops.addToSelectedSearchItems(items[i]);
          }
        } else {
          if (distanceToFirst < distanceToLast) {
            for (let i = targetIndex; i <= lastIndex; i++) {
              searchState.ops.addToSelectedSearchItems(items[i]);
            }
          } else {
            for (let i = firstIndex; i <= targetIndex; i++) {
              searchState.ops.addToSelectedSearchItems(items[i]);
            }
          }
        }
      };

      if (!tryPreviewItem(item, timeInMsHint, commitSelection)) {
        // Unsaved changes etc prevented preview
        return;
      }

      commitSelection();
      break;
    }
  }
}

function toggleSearchItemSelection(
  services: {
    searchState: SearchState,
  },
  item: SearchResultHit,
  timeInMsHint: number | null,
  isMultiSelectionMode: boolean,
  tryPreviewItem: (item: SearchResultHit, timeInMsHint: number | null, commitSelection: () => void) => boolean,
) {
  const { searchState } = services;

  invariant(item, 'Expected selected item to be present in search result state!');
  if (!isMultiSelectionMode) {
    const commitSelection = () => {
      searchState.ops.setCurrentItemId(item.id);
      searchState.ops.clearSelectedSearchItems();
      searchState.ops.addToSelectedSearchItems(item);
    };

    // In regular select mode we always want to load the item we clicked on
    if (!tryPreviewItem(item, timeInMsHint, commitSelection)) {
      // Preview was blocked. For instance because QuickPreview had unsaved metadata changes
      return;
    }

    commitSelection();
    return;
  }

  const toSelect = !searchState.ops.isSelectedSearchItem(item);
  if (toSelect) {
    const commitSelection = () => {
      searchState.ops.setCurrentItemId(item.id);
      searchState.ops.addToSelectedSearchItems(item);
    };

    if (!tryPreviewItem(item, timeInMsHint, commitSelection)) {
      return false;
    }

    commitSelection();

    return;
  }

  const nextItem = searchState.reactive.selectedSearchItems
    .filter(selectedItem => selectedItem.id !== item.id)
    .pop();

  const commitSelection = () => {
    searchState.ops.setCurrentItemId(nextItem?.id || null);
    searchState.ops.removeFromSelectedSearchItems(item);
  };

  // If this is undefined it means the user has deselected the last item.
  // In this case we just keep the last item in the preview so we don't
  // dispatch a preview item call
  if (nextItem !== undefined) {
    if (!tryPreviewItem(nextItem, null, commitSelection)) {
      return;
    }
  }

  commitSelection();
}
